import { FlowContextProvider, SessionSync, StepWrapper, useSessionStorage } from '@betterment/js-runtime';
import { useRequiredParam } from '@betterment/shared-components/src/hooks/useRequiredParam';
import { Route, Routes, useMatch } from 'react-router-dom';

import { FourOhOneKBlockedErrorPage } from './error-pages/FourOhOneKBlockedErrorPage';
import { PendingActionErrorPage } from './error-pages/PendingActionErrorPage';
import { NewSellOrder } from './NewSellOrder';
import { ReviewOrder } from './ReviewOrder';
import { SellAllWarning } from './SellAllWarning';
import { SellOrderFlowValues, toReviewOrderProps, toSellAllWarningProps } from './types';

import { IdempotencyProviderWithFeatureRegistry } from '../../../providers/IdempotencyProviderWithFeatureRegistry';
import urlBuilder from '../../../Shared/url-builder';

export function useSellOrderFlowSessionStorageKey(): string {
  const householdId = useRequiredParam('householdId');
  const accountId = useRequiredParam('accountId');
  const financialSecurityId = useRequiredParam('financialSecurityId');

  return `${householdId}-${accountId}-${financialSecurityId}-sell-order`;
}

export function SellOrderFlow() {
  const householdId = useRequiredParam('householdId');
  const accountId = useRequiredParam('accountId');
  const match = useMatch(`${urlBuilder.getSecuritySellOrderFlow.path}/*`);
  const returnToPath = urlBuilder.getAccountOverviewPage(householdId, accountId);

  const sessionKey = useSellOrderFlowSessionStorageKey();
  const initialValues = useSessionStorage<SellOrderFlowValues>(sessionKey).values;

  const steps = {
    new: 'new',
    review: 'review',
    fourOhOneKBlocked: '401k_blocked',
    pendingAction: 'pending_action',
    sellAllWarning: 'sell_all_warning',
  };

  return (
    <IdempotencyProviderWithFeatureRegistry featureName="securityOrder">
      <FlowContextProvider<SellOrderFlowValues>
        basePath={match!.pathnameBase}
        routePath=""
        returnToPath={returnToPath}
        initialValues={initialValues ?? undefined}
      >
        <SessionSync sessionKey={sessionKey} />
        <Routes>
          <Route
            path={`/${steps.new}`}
            element={
              <StepWrapper<SellOrderFlowValues> name={steps.new}>
                {stepProps => {
                  if (stepProps.flowState.currentStep !== steps.new) {
                    return null;
                  }
                  return <NewSellOrder {...stepProps} />;
                }}
              </StepWrapper>
            }
          />
          <Route
            path={`/${steps.review}`}
            element={
              <StepWrapper<SellOrderFlowValues> name={steps.review}>
                {stepProps => {
                  if (stepProps.flowState.currentStep !== steps.review) {
                    return null;
                  }
                  return <ReviewOrder {...toReviewOrderProps(stepProps)} />;
                }}
              </StepWrapper>
            }
          />
          <Route
            path={`/${steps.fourOhOneKBlocked}`}
            element={
              <StepWrapper<SellOrderFlowValues> name={steps.fourOhOneKBlocked}>
                {stepProps => {
                  if (stepProps.flowState.currentStep !== steps.fourOhOneKBlocked) {
                    return null;
                  }
                  return <FourOhOneKBlockedErrorPage {...stepProps} />;
                }}
              </StepWrapper>
            }
          />
          <Route
            path={`/${steps.pendingAction}`}
            element={
              <StepWrapper<SellOrderFlowValues> name={steps.pendingAction}>
                {stepProps => {
                  if (stepProps.flowState.currentStep !== steps.pendingAction) {
                    return null;
                  }
                  return <PendingActionErrorPage {...stepProps} />;
                }}
              </StepWrapper>
            }
          />
          <Route
            path={`/${steps.sellAllWarning}`}
            element={
              <StepWrapper<SellOrderFlowValues> name={steps.sellAllWarning}>
                {stepProps => {
                  if (stepProps.flowState.currentStep !== steps.sellAllWarning) {
                    return null;
                  }
                  return <SellAllWarning {...toSellAllWarningProps(stepProps)} />;
                }}
              </StepWrapper>
            }
          />
        </Routes>
      </FlowContextProvider>
    </IdempotencyProviderWithFeatureRegistry>
  );
}
